import React, { useEffect, useState, useCallback, useRef } from "react";

import axios from "axios";

import { useNavigate, useSearchParams } from "react-router-dom";

import { 
  Radio, 
  Users, 
  Logs,
  EyeOff,
  SmartphoneNfc,
  Siren,
  Menu,
  X,
  ArrowLeft,
  Server,
  Settings2,
  TrendingUp,
  BookOpen,
  Activity,
  RotateCcw
} from 'lucide-react';

import SummarySection from "./SummarySection";
import TranscriptionEngine from "./TranscriptionEngine";
import KeywordsAndTagsSection from "./KeywordsAndTagsSection";

import HallucinationsSection from "./HallucinationsSection";

import ChannelsAndStationsSection from "./ChannelsAndStationsSection";

import UserManagementSection from "./UserManagementSection";

import GlobalSettingsSection from "./GlobalSettingsSection";

import SystemSection from "./SystemSection";

import F1TerminalLogs from '../Logs/F1TerminalLogs';

import ReportsComponent from './ReportsManagement';

import ScannerTable from './ScannerTabel';

import RecorderDevices from './RecorderDevices';

import AudioLevelVisualizer from './AudioLevelVisualizer';

import BackupProgressModal from './BackupProgressModal';

import InlineDocumentation from '../Documentation/InlineDocumentation';
import InteractiveUserGuide from '../Documentation/InteractiveUserGuide';

import { toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from "../AuthContext";

import { usePermissions } from "../hooks/usePermissions";

import logger from "../../utils/logger";



const SettingsPage = ({ isDarkMode, timezone, timeFormat, setTimeFormat, reverseSort, setReverseSort, onSettingsChange = () => {} }) => {

  const [loading, setLoading] = useState(true);

  const [keywords, setKeywords] = useState([]);

  const [newKeyword, setNewKeyword] = useState('');

  const [activeSection, setActiveSection] = useState('summary');

  const [showBackupModal, setShowBackupModal] = useState(false);

  const [globalSettings, setGlobalSettings] = useState({

    target_language: "english",

    model: "medium.en",

    hallucination: "True",

    transcribe_local: "True",

    transcribe_openai: "True",

    transcribe_node: "True",

    enable_uniden_scanners: "False",

    enable_edge_devices: "True",

    enable_usb_audio_devices: "False",

    enable_s3_upload: "False",

    s3_endpoint_url: "",

    s3_access_key: "",

    s3_secret_key: "",

    s3_region: "us-east-1",

    s3_bucket_name: "",

    s3_backup_time: "03:00",

    // Samba / network share backup

    enable_samba_backup: "False",

    samba_share_path: "",

    samba_username: "",

    samba_password: ""

  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [userRole, setUserRole] = useState(null);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const toastIdRef = useRef(null);

  const toastTimeoutRef = useRef(null);

  const saveTimeoutRef = useRef({}); // Track debounce timers per field

  const lastSavedValueRef = useRef({}); // Track last saved value per field to avoid unnecessary saves

  const edgeServerEndpoint = localStorage.getItem("EDGE_SERVER_ENDPOINT") || 

                           process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "";

  const API_BASE_URL = `${edgeServerEndpoint}`;

  const { logout, user } = useAuth();

  const { hasPermission, loading: permissionsLoading } = usePermissions(edgeServerEndpoint);

  const [isRestartingService, setIsRestartingService] = useState(false);

  const handleRestartService = async () => {
    if (isRestartingService) return;
    setIsRestartingService(true);
    try {
      // API_BASE_URL already includes the /api prefix (used elsewhere like `${API_BASE_URL}/settings`)
      await axios.post(`${API_BASE_URL}/settings/restart-service`);
      showToast('Service restart triggered successfully. The UI may disconnect briefly.', 'success');
    } catch (error) {
      logger.error('Error restarting system service:', error);
      showToast('Failed to restart service. Please try again or check logs.', 'error');
    } finally {
      setIsRestartingService(false);
    }
  };



  logger.debug("SidebarFooter user:", user.username);



  // Define all sidebar items

  const allSidebarItems = [

    { id: 'summary', label: 'Summary', icon: TrendingUp },

    
    { id: 'recorders', label: 'Recorders', icon: Radio },
    
    { id: 'keywords-tags', label: 'Keyword tracking', icon: Siren },
    
    { id: 'user-management', label: 'Users', icon: Users },
    
    { id: 'global', label: 'Configurations', icon: Server },
    
    { id: 'system', label: 'System', icon: Settings2 },
    { id: 'transcription-engine', label: 'Transcription Engine', icon: Activity },

    { id: 'Logs', label: 'Logs', icon: Logs },


  ];



  // Filter sidebar items based on permissions

  const sidebarItems = (() => {

    // If userRole is still loading, show all items by default (they'll be filtered later)

    // This prevents the menu from appearing empty while permissions load

    if (userRole === null || permissionsLoading) {

      return allSidebarItems;

    }

    

    // Admin always sees all items

    if (userRole === 'admin') {

      return allSidebarItems;

    }

    

    // For non-admins, filter based on access_settings permission

    if (!hasPermission('access_settings')) {

      return [];

    }

    

    // If has access_settings, show all items

    return allSidebarItems;

  })();



  useEffect(() => {

    const fetchUserRole = async () => {

      try {

        const response = await axios.get(`${API_BASE_URL}/api/users/${user.username}`);

        setUserRole(response.data[user.username]?.role || 'member'); // Default to member if role not found

      } catch (error) {

        logger.error('Error fetching user role:', error);

        setUserRole('member'); // Default to member on error

      }

    };

    if (user?.username) {

      fetchUserRole();

    }

  }, [user?.username, API_BASE_URL]);



  // Redirect if user doesn't have access_settings permission

  useEffect(() => {

    if (!permissionsLoading && userRole !== null && userRole !== 'admin') {

      if (!hasPermission('access_settings')) {

        navigate('/');

      }

    }

  }, [permissionsLoading, userRole, hasPermission, navigate]);



  // Handle URL parameters and set default active section

  useEffect(() => {

    // Don't process if userRole is still loading

    if (userRole === null) {

      return;

    }



    let tabFromUrl = searchParams.get('tab');

    logger.debug('URL tab parameter:', tabFromUrl);

    logger.debug('Current user role:', userRole);

    logger.debug('Available sidebar items:', sidebarItems.map(item => item.id));

    

    // Handle legacy URL redirects

    if (tabFromUrl === 'channel' || tabFromUrl === 'frequency' || tabFromUrl === 'channels-stations') {

      tabFromUrl = 'recorders';

      setSearchParams({ tab: 'recorders' });

    } else if (tabFromUrl === 'keywords' || tabFromUrl === 'tag') {

      tabFromUrl = 'keywords-tags';

      setSearchParams({ tab: 'keywords-tags' });

    } else if (tabFromUrl === 'user' || tabFromUrl === 'profiles') {

      tabFromUrl = 'user-management';

      setSearchParams({ tab: 'user-management' });

    } else if (tabFromUrl === 'hallucination') {

      tabFromUrl = 'global';

      setSearchParams({ tab: 'global', globalTab: 'audio-post-processing' });

    } else if (tabFromUrl === 'Branding' || tabFromUrl === 'backup-restore' || 

               tabFromUrl === 'interfaces' || tabFromUrl === 'danger') {

      // Map old section IDs to global tab IDs

      let globalTab = 'display-language';

      if (tabFromUrl === 'backup-restore') {

        globalTab = 'backup-restore';

      } else if (tabFromUrl === 'interfaces') {

        globalTab = 'interfaces';

      } else if (tabFromUrl === 'danger') {

        globalTab = 'danger-zone';

      } else if (tabFromUrl === 'Branding') {

        globalTab = 'branding';

      }

      tabFromUrl = 'global';

      setSearchParams({ tab: 'global', globalTab });

    } else if (tabFromUrl === 'hotspot-configuration' || tabFromUrl === 'hotspot') {

      tabFromUrl = 'global';

      setSearchParams({ tab: 'global', globalTab: 'hotspot-configuration' });

    }

    

    if (tabFromUrl) {

      // Check if the tab is valid for the current user role

      const isValidTab = sidebarItems.some(item => item.id === tabFromUrl);

      logger.debug('Is valid tab:', isValidTab);

      if (isValidTab) {

        setActiveSection(tabFromUrl);

        return;

      }

    }

    

    // If no valid tab in URL, set default based on user role

    if (userRole === 'member') {

      setActiveSection('summary');

      // Update URL to reflect the default tab

      setSearchParams({ tab: 'summary' });

    } else {

      // For non-members, default to summary if no tab specified

      if (!tabFromUrl) {

        setSearchParams({ tab: 'summary' });

      }

    }

  }, [userRole, searchParams, setSearchParams, sidebarItems]);



  // Handle resize for mobile-to-desktop transition

  useEffect(() => {

    const handleResize = () => {

      if (window.innerWidth >= 768 && isSidebarOpen) {

        setIsSidebarOpen(false);

      }

    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);

  }, [isSidebarOpen]);



  const showToast = useCallback((message, type = 'success') => {

    // Prevent multiple rapid calls

    if (toastTimeoutRef.current) {

      clearTimeout(toastTimeoutRef.current);

    }

    

    // Dismiss any existing toast safely

    if (toastIdRef.current) {

      try {

        toast.dismiss(toastIdRef.current);

      } catch (error) {

        logger.error('Error dismissing existing toast:', error);

      }

      toastIdRef.current = null;

    }

    

    toastTimeoutRef.current = setTimeout(() => {

      const toastOptions = {

        position: 'top-right',

        autoClose: 8000,

        hideProgressBar: false,

        closeOnClick: true,

        pauseOnHover: true,

        draggable: true,

        theme: isDarkMode ? "dark" : "light",

        onClose: () => { 

          // Safely clear the reference

          toastIdRef.current = null; 

        },

        onOpen: () => {

          // Ensure we have a valid toast ID

          if (!toastIdRef.current) {

            toastIdRef.current = null;

          }

        }

      };

      

      try {

        const toastId = type === 'success' 

        ? toast.success(message, toastOptions)

        : toast.error(message, toastOptions);

        

        // Only set the ID if the toast was created successfully

        if (toastId) {

          toastIdRef.current = toastId;

        }

      } catch (error) {

        logger.error('Toast error:', error);

        toastIdRef.current = null;

      }

    }, 100);

  }, [isDarkMode]);



  useEffect(() => {

    return () => {

      if (toastTimeoutRef.current) {

        clearTimeout(toastTimeoutRef.current);

        toastTimeoutRef.current = null;

      }

      if (toastIdRef.current) {

        try {

          toast.dismiss(toastIdRef.current);

        } catch (error) {

          logger.error('Error dismissing toast:', error);

        }

        toastIdRef.current = null;

      }

    };

  }, []);



  const fetchSettings = async () => {

    try {

      const response = await axios.get(`${API_BASE_URL}/settings`);

      const settingsData = response.data;

      setGlobalSettings({

        target_language: settingsData.global_target_language || "english",

        model: settingsData.global_model || "base.en",

        transcribe_local: settingsData.global_transcribe_local || "True",

        transcribe_openai: settingsData.global_transcribe_openai || "False",

        transcribe_node: settingsData.global_transcribe_node || "True",

        enable_api_fallback: settingsData.global_enable_api_fallback || "False",

        hallucination: settingsData.global_hallucination || "True",

        timezone: settingsData.global_timezone || "UTC",

        enable_uniden_scanners: settingsData.global_enable_uniden_scanners || "False",

        enable_edge_devices: settingsData.global_enable_edge_devices || "True",

        enable_usb_audio_devices: settingsData.global_enable_usb_audio_devices || "False",

        enable_s3_upload: settingsData.global_enable_s3_upload || "False",

        s3_endpoint_url: settingsData.s3_endpoint_url || "",

        // Masked keys from backend show as '***', convert to empty string for password fields

        s3_access_key: (settingsData.s3_access_key && settingsData.s3_access_key !== '***') ? settingsData.s3_access_key : "",

        s3_secret_key: (settingsData.s3_secret_key && settingsData.s3_secret_key !== '***') ? settingsData.s3_secret_key : "",

        s3_region: settingsData.s3_region || "us-east-1",

        s3_bucket_name: settingsData.s3_bucket_name || "",

        s3_backup_time: settingsData.s3_backup_time || "03:00",

        // Samba / network share backup

        enable_samba_backup: settingsData.samba_backup_enabled || "False",

        samba_share_path: settingsData.samba_share_path || "",

        samba_username: settingsData.samba_username || "",

        // Keep '***' as-is when password is masked from backend (will display as ****** in UI)

        samba_password: settingsData.samba_password || "",

        // Duplicate file visibility

        global_show_duplicate_files: settingsData.global_show_duplicate_files || "False",

        // Inbox view settings

        inbox_view_mode: settingsData.global_inbox_view_mode || "pagination",

        inbox_records_per_page: settingsData.global_inbox_records_per_page || 10,

        // Transcription API endpoint path (host is fixed in UI)
        api_endpoint: settingsData.global_api_endpoint || "/api/v1/transcribe/openai"

      });

      setKeywords(Array.isArray(settingsData.keywords) ? settingsData.keywords : []);

    } catch (error) {

      logger.error('Error fetching settings:', error);

      showToast('Error loading settings!', 'error');

    }

  };



  useEffect(() => {

    const loadData = async () => {

      setLoading(true);

      try {

        await fetchSettings();

      } catch (error) {

        logger.error('Error loading data:', error);

      } finally {

        setLoading(false);

      }

    };

    loadData();

  }, []);



  const handleAddKeyword = async () => {

    const keyword = newKeyword.trim();

    if (!keyword) return;

    try {

      const response = await axios.post(`${API_BASE_URL}/settings/keywords`, { keyword });

      setKeywords(response.data?.keywords || [...keywords, keyword]);

      setNewKeyword('');

      showToast('Keyword added successfully!');

    } catch (error) {

      logger.error('Error adding keyword:', error);

      showToast(error.response?.data?.error || 'Error adding keyword!', 'error');

    }

  };



  const handleRemoveKeyword = async (keyword) => {

    try {

      const response = await axios.delete(`${API_BASE_URL}/settings/keywords/${keyword}`);

      setKeywords(response.data?.keywords || keywords.filter(k => k !== keyword));

      showToast('Keyword removed successfully!');

    } catch (error) {

      logger.error('Error removing keyword:', error);

      showToast(error.response?.data?.error || 'Error removing keyword!', 'error');

    }

  };



  // Helper function to navigate to a specific tab

  const navigateToTab = (tabId) => {

    setActiveSection(tabId);

    setSearchParams({ tab: tabId });

  };



  if (loading) {

    return (

      <div className="flex justify-center items-center min-h-screen">

        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />

      </div>

    );

  }



  // Show loading while permissions are being fetched

  if (permissionsLoading || userRole === null) {

    return (

      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} flex items-center justify-center`}>

        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>

      </div>

    );

  }



  // Check if user has access (admin or has access_settings permission)

  if (userRole !== 'admin' && !hasPermission('access_settings')) {

    return (

      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} flex items-center justify-center`}>

        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center`}>

          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>

            Access Denied

          </h2>

          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>

            You don't have permission to access settings. Please contact your administrator.

          </p>

          <button

            onClick={() => navigate('/')}

            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"

          >

            Go to Home

          </button>

        </div>

      </div>

    );

  }



  return (

    <div className={`${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'} min-h-screen flex flex-col md:flex-row`}>



      {/* Mobile Menu Button */}

      <div className="lg:hidden fixed top-4 left-4 z-50">

        <button 

          onClick={() => setIsSidebarOpen(!isSidebarOpen)}

          className={`p-2 rounded-md shadow-lg ${

            isDarkMode 

              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 

              : 'bg-white text-gray-600 hover:bg-gray-100'

          }`}

        >

          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}

        </button>

      </div>



      {/* Sidebar */}

      <div
        className={`sidebar-container
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} 
          border-r w-64 flex-shrink-0
          lg:static lg:h-auto
          fixed top-0 left-0 h-full
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 z-40`}
      >

        <div className="flex flex-col h-full">

          <div className="p-6 border-b border-gray-200">

            <div className="flex items-center justify-between">

              <div className="flex items-center gap-3">

                <Server className="w-6 h-6 text-blue-600" />

                <h1 className="text-xl font-semibold">Settings</h1>

              </div>

              <button

                onClick={() => {

                  navigate("/");

                  setIsSidebarOpen(false);

                }}

                className={`p-2 rounded-lg transition-colors ${

                  isDarkMode 

                    ? 'text-gray-400 hover:text-white hover:bg-gray-700' 

                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'

                }`}

                title="Back to Dashboard"

              >

                <ArrowLeft className="w-5 h-5" />

              </button>

            </div>

          </div>

          

          <nav className="flex-1 p-4 overflow-y-auto">

            <div className="space-y-1">

              {sidebarItems.map((item) => {

                const Icon = item.icon;

                return (

                  <button

                    key={item.id}

                    onClick={() => {

                      setActiveSection(item.id);

                      setSearchParams({ tab: item.id });

                      setIsSidebarOpen(false);

                    }}

                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${

                      activeSection === item.id 

                        ? 'bg-blue-600 text-white' 

                        : `${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`

                    }`}

                  >

                    <Icon className="w-5 h-5" />

                    {item.label}

                  </button>

                );

              })}

            </div>

          </nav>

          {/* Sidebar footer restart button */}
          <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
            <button
              onClick={handleRestartService}
              disabled={isRestartingService}
              className={`w-full inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                isDarkMode
                  ? 'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-900 disabled:text-gray-400'
                  : 'bg-red-500 hover:bg-red-600 text-white disabled:bg-red-200 disabled:text-gray-500'
              } disabled:cursor-not-allowed`}
              title="Restart Boondock Edge service"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${isRestartingService ? 'animate-spin' : ''}`} />
              {isRestartingService ? 'Restarting…' : 'Restart service'}
            </button>
          </div>

        </div>

      </div>



      {/* Mobile Overlay */}

      {isSidebarOpen && (

        <div 

          className="md:hidden fixed inset-0 bg-black/50 z-30"

          onClick={() => setIsSidebarOpen(false)}

        />

      )}



      {/* Main Content */}

      <div className="flex-1 overflow-auto">

        <div className="bg-gray-100">

        {/* <div className="p-8 md:p-8 pt-20 md:pt-8"> */}

          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg  p-6`}>

            {activeSection === 'summary' && (

              <SummarySection

                isDarkMode={isDarkMode}

                edgeServerEndpoint={edgeServerEndpoint}

                timezone={timezone}

                globalSettings={globalSettings}

                handleGlobalChange={async (field, value, showNotification = true) => {

                  // Update local state immediately for responsive UI

                  setGlobalSettings(prev => ({ ...prev, [field]: value }));

                  

                  // Clear any existing timeout for this field

                  if (saveTimeoutRef.current[field]) {

                    clearTimeout(saveTimeoutRef.current[field]);

                  }

                  

                  // Debounce the API call and notification

                  saveTimeoutRef.current[field] = setTimeout(async () => {

                    try {

                      // Skip save if value hasn't changed

                      if (lastSavedValueRef.current[field] === value) {

                        return;

                      }

                      

                      // S3, Samba, and host settings use different field naming

                      let apiField = field;

                      if (field === 'enable_s3_upload') {

                        apiField = 'global_enable_s3_upload';

                      } else if (field === 'enable_samba_backup') {

                        apiField = 'samba_backup_enabled';

                      } else if (field.startsWith('s3_') || field.startsWith('host_')) {

                        apiField = field; // s3_* and host_* fields don't need global_ prefix

                      } else if (field.startsWith('global_')) {

                        apiField = field; // Already has global_ prefix

                      } else {

                        apiField = `global_${field}`;

                      }

                      

                      // For password fields, only send if value is provided (not empty)

                      // Backend will preserve existing values if empty string is sent

                      const payload = { [apiField]: value };

                      

                      await axios.put(`${API_BASE_URL}/settings`, payload);

                      lastSavedValueRef.current[field] = value;

                      

                      // Only show toast if explicitly requested (e.g., on blur or for non-text inputs)

                      if (showNotification) {

                        showToast('Settings updated successfully!');

                      }

                    } catch (error) {

                      logger.error('Error updating global settings:', error);

                      showToast('Error updating global settings!', 'error');

                    }

                  }, 1000); // 1 second debounce

                }}

              />

            )}

            {activeSection === 'transcription-engine' && (
              <TranscriptionEngine
                isDarkMode={isDarkMode}
                edgeServerEndpoint={edgeServerEndpoint}
              />
            )}

            {activeSection === 'recorders' && (

              <ChannelsAndStationsSection

                isDarkMode={isDarkMode}

                edgeServerEndpoint={edgeServerEndpoint}

                recordersEnabled={String(globalSettings.enable_edge_devices).toLowerCase() === 'true'}

                globalSettings={globalSettings}

              />

            )}

            {activeSection === 'hallucination' && (

              <HallucinationsSection

                keywords={keywords}

                newKeyword={newKeyword}

                setNewKeyword={setNewKeyword}

                handleAddKeyword={handleAddKeyword}

                handleRemoveKeyword={handleRemoveKeyword}

                globalSettings={globalSettings}

                handleGlobalChange={async (field, value) => {

                  try {

                    await axios.put(`${API_BASE_URL}/settings`, { [`global_${field}`]: value });

                    setGlobalSettings(prev => ({ ...prev, [field]: value }));

                    showToast('Settings updated successfully!');

                  } catch (error) {

                    logger.error('Error updating settings:', error);

                    showToast('Error updating settings!', 'error');

                  }

                }}

                isDarkMode={isDarkMode}

              />

            )}

            {activeSection === 'keywords-tags' && (

              <KeywordsAndTagsSection

                keywords={keywords}

                newKeyword={newKeyword}

                setNewKeyword={setNewKeyword}

                handleAddKeyword={handleAddKeyword}

                handleRemoveKeyword={handleRemoveKeyword}

                isDarkMode={isDarkMode}

                edgeServerEndpoint={edgeServerEndpoint}

              />

            )}

            {activeSection === 'user-management' && (

              <UserManagementSection 

                edgeServerEndpoint={edgeServerEndpoint} 

                isDarkMode={isDarkMode} 

              />

            )}

            {activeSection === 'global' && (

              <GlobalSettingsSection

                globalSettings={globalSettings}

                handleGlobalChange={async (field, value, showNotification = true) => {

                  // Update local state immediately for responsive UI

                  setGlobalSettings(prev => ({ ...prev, [field]: value }));

                  

                  // Clear any existing timeout for this field

                  if (saveTimeoutRef.current[field]) {

                    clearTimeout(saveTimeoutRef.current[field]);

                  }

                  

                  // Debounce the API call and notification

                  saveTimeoutRef.current[field] = setTimeout(async () => {

                    try {

                      // Skip save if value hasn't changed

                      if (lastSavedValueRef.current[field] === value) {

                        return;

                      }

                      

                      // S3, Samba, and host settings use different field naming

                      let apiField = field;

                      if (field === 'enable_s3_upload') {

                        apiField = 'global_enable_s3_upload';

                      } else if (field === 'enable_samba_backup') {

                        apiField = 'samba_backup_enabled';

                      } else if (field.startsWith('s3_') || field.startsWith('host_')) {

                        apiField = field; // s3_* and host_* fields don't need global_ prefix

                      } else if (field.startsWith('global_')) {

                        apiField = field; // Already has global_ prefix

                      } else {

                        apiField = `global_${field}`;

                      }

                      

                      // For password fields, only send if value is provided (not empty)

                      // Backend will preserve existing values if empty string is sent

                      const payload = { [apiField]: value };

                      

                      await axios.put(`${API_BASE_URL}/settings`, payload);

                      lastSavedValueRef.current[field] = value;

                      

                      // Refresh all data if duplicate visibility setting changed

                      if (field === 'global_show_duplicate_files') {

                        onSettingsChange();

                      }

                      

                      // Only show toast if explicitly requested (e.g., on blur or for non-text inputs)

                      if (showNotification) {

                        showToast('Global settings updated successfully!');

                      }

                    } catch (error) {

                      logger.error('Error updating global settings:', error);

                      showToast('Error updating global settings!', 'error');

                    }

                  }, 1000); // 1 second debounce

                }}

                isDarkMode={isDarkMode}

                timezone={timezone}

                timeFormat={timeFormat}

                setTimeFormat={setTimeFormat}

                reverseSort={reverseSort}

                setReverseSort={setReverseSort}

                user={user}

                edgeServerEndpoint={edgeServerEndpoint}

                showToast={showToast}

                handleBackupNow={() => setShowBackupModal(true)}

                keywords={keywords}

                newKeyword={newKeyword}

                setNewKeyword={setNewKeyword}

                handleAddKeyword={handleAddKeyword}

                handleRemoveKeyword={handleRemoveKeyword}

              />

            )}

            {activeSection === 'system' && (

              <SystemSection

                isDarkMode={isDarkMode}

                edgeServerEndpoint={edgeServerEndpoint}

                showToast={showToast}

                globalSettings={globalSettings}

                handleGlobalChange={async (field, value, showNotification = true) => {

                  // Update local state immediately for responsive UI

                  setGlobalSettings(prev => ({ ...prev, [field]: value }));

                  

                  // Clear any existing timeout for this field

                  if (saveTimeoutRef.current[field]) {

                    clearTimeout(saveTimeoutRef.current[field]);

                  }

                  

                  // Debounce the API call and notification

                  saveTimeoutRef.current[field] = setTimeout(async () => {

                    try {

                      // Skip save if value hasn't changed

                      if (lastSavedValueRef.current[field] === value) {

                        return;

                      }

                      

                      // S3, Samba, and host settings use different field naming

                      let apiField = field;

                      if (field === 'enable_s3_upload') {

                        apiField = 'global_enable_s3_upload';

                      } else if (field === 'enable_samba_backup') {

                        apiField = 'samba_backup_enabled';

                      } else if (field.startsWith('s3_') || field.startsWith('host_')) {

                        apiField = field; // s3_* and host_* fields don't need global_ prefix

                      } else if (field.startsWith('global_')) {

                        apiField = field; // Already has global_ prefix

                      } else {

                        apiField = `global_${field}`;

                      }

                      

                      // For password fields, only send if value is provided (not empty)

                      // Backend will preserve existing values if empty string is sent

                      const payload = { [apiField]: value };

                      

                      await axios.put(`${API_BASE_URL}/settings`, payload);

                      lastSavedValueRef.current[field] = value;

                      

                      // Only show toast if explicitly requested (e.g., on blur or for non-text inputs)

                      if (showNotification) {

                        showToast('Settings updated successfully!');

                      }

                    } catch (error) {

                      logger.error('Error updating settings:', error);

                      showToast('Error updating settings!', 'error');

                    }

                  }, 1000); // 1 second debounce

                }}

                timezone={timezone}

                timeFormat={timeFormat}

                setTimeFormat={setTimeFormat}

                reverseSort={reverseSort}

                setReverseSort={setReverseSort}

                user={user}

                handleBackupNow={() => setShowBackupModal(true)}

                keywords={keywords}

                newKeyword={newKeyword}

                setNewKeyword={setNewKeyword}

                handleAddKeyword={handleAddKeyword}

                handleRemoveKeyword={handleRemoveKeyword}

              />

            )}

            {activeSection === 'scanner' && (

              <ScannerTable edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />

            )}

            {/* {activeSection === 'reports' && (

              <ReportsComponent edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />

            )} */}

            {activeSection === 'audio' && (

              <AudioLevelVisualizer edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />

            )}



            {activeSection === 'Logs' && (

              <F1TerminalLogs edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />

            )}


          </div>

        </div>

      </div>



      {/* Backup Progress Modal */}

      <BackupProgressModal

        isOpen={showBackupModal}

        onClose={() => setShowBackupModal(false)}

        edgeServerEndpoint={edgeServerEndpoint}

        isDarkMode={isDarkMode}

        globalSettings={globalSettings}

      />

    </div>

  );

};



export default SettingsPage;